import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { EPISODE, HEALTH_PLAN, PLAN_TYPE } from 'constants/classifications';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { Classification } from 'models/flexible/Classification';
import { adminClassificationsQueryKeys, adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import type { AdminClassificationIndexFields } from 'services/api/fields';
import { BodySmall } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';

import ClassificationModal from './ClassificationModal';
import { classificationPageTitle, classificationTitle } from './helpers';

const CLASSIFICATION_INCLUDES = ['childClassifications', 'parentClassification'] as const;

type ClassificationRow = Classification<AdminClassificationIndexFields<(typeof CLASSIFICATION_INCLUDES)[number]>>;

function buildConfig(type: string): AdminListPageConfig<ClassificationRow> {
  const title = classificationTitle(type);
  const pageTitle = classificationPageTitle(type);

  const filters = [
    ...(type === PLAN_TYPE
      ? [
          {
            key: 'parentClassificationId',
            label: 'Plan',
            type: 'dropdown' as const,
            fetchConfig: {
              queryKey: ['adminClassifications', 'health_plan', 'filter'],
              fetchFn: adminIndexClassifications,
              params: { type: HEALTH_PLAN, sortBy: 'name asc' },
            },
            getOptionLabel: (o: any) => o.name,
            getOptionValue: (o: any) => o.id,
          },
        ]
      : []),
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown' as const,
      fetchConfig: {
        queryKey: ['adminClients', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
  ];

  const columns = (() => {
    switch (type) {
      case HEALTH_PLAN:
        return [
          {
            id: 'name',
            header: 'Plan',
            render: (row: ClassificationRow) => <strong>{row.name}</strong>,
          },
          {
            id: 'childClassifications',
            header: 'Associated Plan Types',
            sortable: false,
            render: (row: ClassificationRow) => (
              <BodySmall>{row.childClassifications?.map((c) => c.name).join(', ') || '—'}</BodySmall>
            ),
          },
        ];
      case PLAN_TYPE:
        return [
          {
            id: 'name',
            header: 'Name',
            render: (row: ClassificationRow) => <strong>{row.name}</strong>,
          },
          {
            id: 'parentClassification',
            header: 'Plan',
            render: (row: ClassificationRow) => <>{row.parentClassification?.name || '—'}</>,
          },
          {
            id: 'clientNames',
            header: 'Associated Clients',
            sortable: false,
          },
        ];
      case EPISODE:
      default:
        return [
          {
            id: 'name',
            header: 'Name',
            render: (row: ClassificationRow) => <strong>{row.name}</strong>,
          },
          {
            id: 'clientNames',
            header: 'Associated Clients',
            sortable: false,
          },
        ];
    }
  })();

  return {
    title: pageTitle,
    queryKey: adminClassificationsQueryKeys.index({ type }),
    fetchFn: (params, signal) => adminIndexClassifications(params, signal, [...CLASSIFICATION_INCLUDES]),
    includes: CLASSIFICATION_INCLUDES,
    fetchParams: {
      defaultSort: 'name asc',
      extra: { type },
    },
    searchPlaceholder: 'Search...',
    showResultCount: true,

    addButton: {
      label: `Add ${title}`,
      permission: 'adminClassificationCreate',
      onClick: (ctx) => ctx.openModal('edit', new Classification({ type })),
    },

    filters,
    columns,

    rowActions: [
      {
        label: 'Edit',
        icon: <PencilIcon width={18} height={18} />,
        permission: 'adminClassificationEdit',
        onClick: (row, { openModal }) => openModal('edit', row),
      },
    ],

    customModals: {
      edit: {
        render: (row, show, onClose, ctx) => (
          <ClassificationModal
            isOpen={show}
            classification={row}
            title={title}
            onCancel={onClose}
            onComplete={() => {
              onClose();
              ctx.refetch();
            }}
          />
        ),
      },
    },
  };
}

const ROUTE_TO_TYPE: Record<string, string> = {
  'episode-types': EPISODE,
  'plan-types': PLAN_TYPE,
  plans: HEALTH_PLAN,
};

export default function Classifications() {
  const { type: routeType } = useParams<{ type: string }>();
  const type = ROUTE_TO_TYPE[routeType!] ?? routeType!;

  const config = useMemo(() => buildConfig(type), [type]);
  const state = useAdminListPage(config);

  return <AdminListPageRenderer config={config} state={state} />;
}
